import React, { Component } from 'react';
import classnames from 'classnames';
import FormControl from '@mui/material/FormControl';
import { WithStyles, withStyles } from '@mui/styles';
import { InputLabel, MenuItem, Select } from '@mui/material';

// Components - Atoms, Molecules, Organisms, Pages
import APSelectItem from '../APSelect/APSelectItem';
// Files in the same folder
import APSelectDropdownStyles from './styles/APSelectDropdown.style';
import { ReactComponent as ChevronDown } from './assets/icon-chevron-down.svg';

type CommissionStatementLabel = {
  firstSection: string;
  secondSection: string;
}

type ReferenceDataLabel = string | CommissionStatementLabel;

type ReferenceData = {
  label: ReferenceDataLabel;
  value: any;
};

interface APSelectDropdownProps extends WithStyles<typeof APSelectDropdownStyles> {
  data?: any,
  label?: string,
  onBlur?: any,
  onChange?: any,
  onFocus?: any,
  name?: string,
  value?: any,
  className?: string,
  disabled?: boolean,
  shouldDisableScrollLock?: boolean
}

class APSelectDropdownRef extends Component<APSelectDropdownProps, {}> {
  anchorRef: any = null;

  state = {
    inputValue: undefined,
    open: false
  };

  static getDerivedStateFromProps(props: APSelectDropdownProps, state: any) {
    if (state.inputValue !== undefined && state.inputValue !== '') {
      return state;
    }

    return {
      ...state,
      inputValue: props.value
    };
  }

  constructor(props: APSelectDropdownProps) {
    super(props);
    this.anchorRef = React.createRef();
    this.onChange = this.onChange.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
  }

  handleClose = () => {
    this.setState({ open: false })
  };

  handleOpen = () => {
    this.setState({ open: true })
  };

  onChange(event: any, selection: any) {
    const {
      name,
      onChange
    } = this.props;

    this.setState({
      inputValue: undefined
    }, () => {
      if (onChange) {
        onChange({
          target: {
            name,
            value: selection !== null ? selection.value : ''
          }
        });
      }
    });
  }

  render() {
    const {
      classes,
      className,
      data,
      label,
      onChange,
      name,
      value: inputValue,
      disabled,
      shouldDisableScrollLock = true
    } = this.props;

    const {
      open
    } = this.state;

    const isCommissionTypeDropdown: boolean = name === 'commissionType';
    const isCommissionStatementTypeDropdown: boolean = name === 'commissionStatementType';
    const isAccountSwitchTypeDropdown: boolean = name === 'accountSwitchType';
    const commissionOrAccountSwitchTypeCondition: boolean = isCommissionTypeDropdown || isCommissionStatementTypeDropdown || isAccountSwitchTypeDropdown;
    const formControlCls: string = classnames(classes.formControlCls, {
      [classes.commissionFormControlCls]: isCommissionTypeDropdown,
      [classes.openCommissionDropdown]: isCommissionTypeDropdown && open,
      [classes.accountSelectDropdown]: isAccountSwitchTypeDropdown
    });
    const menuItemClass: string = classnames(classes.menuItem, {
      [classes.commissionMenuItem]: isCommissionTypeDropdown,
      [classes.commissionStatementMenuItem]: isCommissionStatementTypeDropdown
    });
    const customPopperClass: string = classnames(classes.selectMenuPopper, {
      [classes.customAccountPopper]: isAccountSwitchTypeDropdown
    });
    const selectClassNames: string = classnames(className, {
      'expanded': open
    });

    return (
      <FormControl className={formControlCls} variant="outlined" ref={this.anchorRef}>
        <InputLabel htmlFor="outlined-select">{label}</InputLabel>
        <Select
          className={selectClassNames}
          native={!commissionOrAccountSwitchTypeCondition}
          open={open}
          onClose={this.handleClose}
          onOpen={this.handleOpen}
          disabled={disabled}
          IconComponent={ChevronDown}
          value={inputValue}
          onChange={onChange}
          label={label}
          inputProps={{
            name: `${name}`,
            id: 'outlined-select',
            "data-testid": "select-input"
          }}
          {...(commissionOrAccountSwitchTypeCondition
            ? {
              MenuProps: {
                anchorEl: this.anchorRef.current,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                classes: { paper: customPopperClass },
                variant: 'menu',
                marginThreshold: 0,
                disableScrollLock: shouldDisableScrollLock,
                MenuListProps: {
                  autoFocusItem: false
                }
              }
            }
            : {}
          )}
        >
          {data.map((option: ReferenceData) => {
            const commonProps = {
              "data-testid": "select-option",
              value: option.value,
              className: menuItemClass
            };
            const label: ReferenceDataLabel = option.label;
            if (typeof label === 'object') {
              return <MenuItem {...commonProps}>
                <span>{label.firstSection}</span>
                &nbsp;
                <span className={classnames(classes.secondSection)}>{label.secondSection}</span>
              </MenuItem>
            }
            return isAccountSwitchTypeDropdown ?
              <MenuItem {...commonProps}>
                <APSelectItem
                  type={'accountSelect'}
                  {...option}
                  aria-selected={inputValue === option.value}
                  key={`${option.label}-${option.value}`}
                />
              </MenuItem>
              : isCommissionTypeDropdown
                ? <MenuItem {...commonProps}>{label}</MenuItem>
                : <option {...commonProps}>{label}</option>
          })}
        </Select>
      </FormControl>
    );
  }
}

export default withStyles(APSelectDropdownStyles)(APSelectDropdownRef);
